import React, { useState } from 'react';
import PropTypes from 'prop-types';
import api from '../../api/client';
import { Button, PasswordInput } from '../common';

/**
 * UserModal - Add or edit user
 */
const UserModal = ({ user, isOwnProfile = false, onClose, onSave }) => {
  const deriveRole = (u) => {
    if (u?.isAdmin) return 'admin';
    if (u?.isManager) return 'manager';
    return 'member';
  };

  const [editedUser, setEditedUser] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    password: '',
    role: deriveRole(user),
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    
    try {
      const data = {
        firstName: editedUser.firstName,
        lastName: editedUser.lastName,
        email: editedUser.email,
      };
      
      // Only include role fields if not editing own profile
      if (!isOwnProfile) {
        data.isAdmin = editedUser.role === 'admin';
        data.isManager = editedUser.role === 'manager';
      }
      
      // Only include password if provided
      if (editedUser.password) {
        data.password = editedUser.password;
      }
      
      if (user?.id) {
        // Edit existing user
        if (!editedUser.password) {
          // Password not required for edit
          await api.put(`/users/${user.id}`, data);
        } else {
          await api.put(`/users/${user.id}`, data);
        }
      } else {
        // Create new user - password required
        if (!editedUser.password) {
          setError('Password is required');
          setSaving(false);
          return;
        }
        await api.post('/users', data);
      }
      onSave();
    } catch (err) {
      setError(err.message || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 className="modal-title">{user ? 'Edit User' : 'Add User'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group">
              <label className="form-label">First Name</label>
              <input
                type="text"
                className="form-input"
                value={editedUser.firstName}
                onChange={(e) => setEditedUser({ ...editedUser, firstName: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name</label>
              <input
                type="text"
                className="form-input"
                value={editedUser.lastName}
                onChange={(e) => setEditedUser({ ...editedUser, lastName: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={editedUser.email}
                onChange={(e) => setEditedUser({ ...editedUser, email: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">
                Password {user && '(leave blank to keep current)'}
              </label>
              <PasswordInput
                value={editedUser.password}
                onChange={(e) => setEditedUser({ ...editedUser, password: e.target.value })}
                required={!user}
                autoComplete="new-password"
              />
            </div>
            {!isOwnProfile && (
              <div className="form-group">
                <label className="form-label">Role</label>
                <select
                  className="form-input"
                  value={editedUser.role}
                  onChange={(e) => setEditedUser({ ...editedUser, role: e.target.value })}
                >
                  <option value="member">Member</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

UserModal.propTypes = {
  user: PropTypes.object,
  isOwnProfile: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default UserModal;
